import { useCallback } from 'react';
//import { toast } from 'react-toastify';
import toast from '../utils/toast';

/**
 * Custom hook for handling permission errors
 * @returns {Object} Object with error handling functions
 */
export const usePermissionError = () => {
  /**
   * Handle 403 permission denied errors
   * @param {Error} error - The error object
   * @param {string} action - The action that was attempted (optional)
   * @param {string} resource - The resource being accessed (optional)
   */
  const handlePermissionError = useCallback((error, action = '', resource = '') => {
    if (error.response && error.response.status === 403) {
      const errorMessage = error.response.data?.message || 'Access denied. You do not have permission to perform this action.';
      const requiredPermission = error.response.data?.requiredPermission;
      
      let toastMessage = errorMessage;
      
      if (action && resource) {
        toastMessage = `Access denied. You do not have permission to ${action} ${resource}.`;
      }
      
      if (requiredPermission) {
        toastMessage += ` Required permission: ${requiredPermission}`;
      }
      
      toast.error(toastMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }, []);

  /**
   * Handle general API errors
   * @param {Error} error - The error object
   * @param {string} customMessage - Custom error message (optional)
   */
  const handleApiError = useCallback((error, customMessage = '') => {
    if (error.response && error.response.status >= 400) {
      const errorMessage = customMessage || error.response.data?.message || 'An error occurred. Please try again.';
      
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }, []);

  /**
   * Show a custom permission denied toast
   * @param {string} action - The action that was denied
   * @param {string} resource - The resource being accessed
   * @param {string} requiredPermission - The required permission (optional)
   */
  const showPermissionDenied = useCallback((action, resource, requiredPermission = '') => {
    let message = `Access denied. You do not have permission to ${action} ${resource}.`;
    
    if (requiredPermission) {
      message += ` Required permission: ${requiredPermission}`;
    }
    
    toast.error(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, []);

  return {
    handlePermissionError,
    handleApiError,
    showPermissionDenied
  };
}; 